// src/pages/TalleresCrear/components/Paso3Resumen.jsx
import React from 'react';
import { Card, Descriptions, Tag, Space, Alert, Empty } from 'antd';
import { 
  CheckCircleOutlined, 
  InfoCircleOutlined, 
  CalendarOutlined, 
  SafetyCertificateOutlined,
  IdcardOutlined,
  CloseCircleOutlined,
  TeamOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getNombreRecinto } from '../utils';

export default function Paso3Resumen({ formValues, recintos }) {
  // Validar que formValues existe
  if (!formValues) {
    return (
      <Alert
        message="Error"
        description="No se encontraron datos del formulario"
        type="error"
        showIcon
      />
    );
  }

  const {
    nombre,
    descripcion,
    recinto,
    tipoActividad,
    tipoDuracionSuscripcion,
    edadMin,
    edadMax,
    // Paso1Fechas dejó de usar el rango `fechasInscripcion` y ahora guarda dos
    // fechas-hora independientes. El resumen seguía leyendo el array viejo, así
    // que siempre mostraba "Inscripciones sin ventana definida".
    inscripcionInicio,
    inscripcionFin,
    fechasActividades,
    esGratuito,
    modalidades,
    liberarCuposPorBaja,
    soloTarjetaVecino,
    requiereMatricula, // 🆕 Extraer campo de matrícula
    tipoTaller, // 🆕 Extraer tipo de taller
    usuariosAutorizados // 🆕 Extraer usuarios autorizados
  } = formValues;

  const getTipoDuracionLabel = (tipo) => {
    const labels = {
      'mes_calendario': '📅 Mes calendario (día a día)',
      'fin_de_mes': '📆 Fin de mes',
      'mes_corrido': '⏱️ Mes corrido (30 días exactos)'
    };
    return labels[tipo] || tipo;
  };

  const planesActivos = modalidades 
    ? Object.entries(modalidades)
        .filter(([_, config]) => config?.activo)
        .map(([plan, config]) => ({ plan, ...config }))
    : [];

  // Validar fechas de inscripción (dos fechas-hora independientes)
  const tieneFechasInscripcion = Boolean(inscripcionInicio && inscripcionFin);

  // Validar periodo de actividades (rango [inicio, fin])
  const tieneFechasActividades = Boolean(
    Array.isArray(fechasActividades) && fechasActividades[0] && fechasActividades[1]
  );

  return (
    <div className="space-y-4">
      <Alert
        message="Revisa la información antes de crear el taller"
        description="Una vez creado, podrás agregar horarios y realizar ajustes adicionales."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />

      {/* Información básica */}
      <Card title="📋 Información básica" size="small">
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Nombre">
            <strong>{nombre || '-'}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Descripción">
            {descripcion || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Recinto">
            {recinto ? (
              <Tag color="blue">{getNombreRecinto(recinto, recintos)}</Tag>
            ) : (
              <Tag color="default">No seleccionado</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de actividad">
            {tipoActividad ? (
              <Tag color="blue">{tipoActividad}</Tag>
            ) : (
              <Tag color="default">No seleccionado</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de duración de suscripción">
            {tipoDuracionSuscripcion ? (
              <div>
                <Tag color="purple">{getTipoDuracionLabel(tipoDuracionSuscripcion)}</Tag>
                <div className="text-xs text-gray-500 mt-1">
                  Define cómo se calculan las fechas de suscripción individual de cada usuario
                </div>
              </div>
            ) : (
              <Tag color="default">No seleccionado</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Rango de edad">
            {edadMin || edadMax ? (
              <Space>
                {edadMin && <Tag>Desde {edadMin} años</Tag>}
                {edadMax && <Tag>Hasta {edadMax} años</Tag>}
              </Space>
            ) : (
              <Tag color="default">Sin restricción</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 🆕 Tipo de Taller y Acceso */}
      <Card 
        title={
          <span>
            <TeamOutlined className="mr-2" />
            Tipo de taller y acceso
          </span>
        } 
        size="small"
      >
        <div className="space-y-3">
          {/* Tipo de Taller */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">Tipo de taller:</span>
            {tipoTaller === 'academia' ? (
              <Tag color="orange" icon={<TeamOutlined />}>
                Academia (Acceso Restringido)
              </Tag>
            ) : (
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                Taller Libre (Acceso Público)
              </Tag>
            )}
          </div>

          {/* Si es academia, mostrar usuarios autorizados */}
          {tipoTaller === 'academia' && usuariosAutorizados && usuariosAutorizados.length > 0 && (
            <Alert
              message={`${usuariosAutorizados.length} usuario(s) autorizado(s)`}
              description={
                <div className="space-y-1 mt-2">
                  {usuariosAutorizados.slice(0, 3).map((usuario, index) => (
                    <div key={index} className="text-xs">
                      • {usuario.nombre} ({usuario.email})
                    </div>
                  ))}
                  {usuariosAutorizados.length > 3 && (
                    <div className="text-xs font-medium mt-1">
                      + {usuariosAutorizados.length - 3} más...
                    </div>
                  )}
                </div>
              }
              type="info"
              showIcon
            />
          )}

          {/* Tarjeta Vecino */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">Tarjeta Vecino:</span>
            {soloTarjetaVecino ? (
              <Tag color="gold" icon={<SafetyCertificateOutlined />}>
                Exclusivo Beneficiarios
              </Tag>
            ) : (
              <Tag color="default">No Requerido</Tag>
            )}
          </div>

          {/* 🆕 Matrícula Requerida */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">Matrícula del recinto:</span>
            {requiereMatricula ? (
              <Tag color="red" icon={<IdcardOutlined />}>
                Obligatoria
              </Tag>
            ) : (
              <Tag color="default" icon={<CloseCircleOutlined />}>
                No Requerida
              </Tag>
            )}
          </div>
        </div>

        {/* Alertas informativas según configuración */}
        <div className="mt-3 space-y-2">
          {tipoTaller === 'academia' && (
            <Alert
              message="⚠️ Taller tipo Academia"
              description="Este taller NO aparecerá en el listado público. Solo los usuarios autorizados podrán verlo e inscribirse."
              type="warning"
              showIcon
              className="text-xs"
            />
          )}

          {soloTarjetaVecino && (
            <Alert
              message="⚠️ Acceso restringido a Tarjeta Vecino"
              description="Solo usuarios con beneficio activo de Tarjeta Vecino podrán inscribirse. Se validará automáticamente antes de la compra."
              type="warning"
              showIcon
              className="text-xs"
            />
          )}

          {requiereMatricula && (
            <Alert
              message="⚠️ Matrícula obligatoria"
              description="Los usuarios deben tener matrícula vigente en este recinto para poder inscribirse. Se validará automáticamente antes de la inscripción."
              type="warning"
              showIcon
              className="text-xs"
            />
          )}

          {!soloTarjetaVecino && !requiereMatricula && tipoTaller === 'libre' && (
            <Alert
              message="✅ Acceso abierto"
              description="Este taller está disponible para todos los usuarios sin restricciones especiales."
              type="success"
              showIcon
              className="text-xs"
            />
          )}
        </div>
      </Card>

      {/* Ventana de inscripción */}
      <Card title="📅 Ventana de inscripción" size="small">
        <div>
          <div className="text-sm font-medium mb-2 text-gray-700">
            Periodo en que los usuarios pueden inscribirse:
          </div>
          {tieneFechasInscripcion ? (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
              <CalendarOutlined className="text-blue-600" />
              <Space size="small" wrap>
                {/* Se muestra la hora: la apertura y el cierre se definen al minuto. */}
                <Tag color="blue">{dayjs(inscripcionInicio).format('DD/MM/YYYY HH:mm')}</Tag>
                <span className="text-gray-500">→</span>
                <Tag color="blue">{dayjs(inscripcionFin).format('DD/MM/YYYY HH:mm')}</Tag>
                <Tag color="cyan">
                  {dayjs(inscripcionFin).diff(dayjs(inscripcionInicio), 'day')} días
                </Tag>
              </Space>
            </div>
          ) : (
            <Alert
              message="Inscripciones sin ventana definida"
              description="Las inscripciones se controlarán manualmente desde el estado del taller. Podrás abrirlas y pausarlas cuando lo necesites."
              type="info"
              showIcon
            />
          )}
        </div>
      </Card>

      {/* Periodo de actividades — se envía al backend (actividadesInicio /
          actividadesFin) pero antes no se mostraba en el resumen. */}
      <Card title="🏃 Periodo de actividades" size="small">
        <div>
          <div className="text-sm font-medium mb-2 text-gray-700">
            Fechas en que se realizará el taller:
          </div>
          {tieneFechasActividades ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded">
              <CalendarOutlined className="text-green-700" />
              <Space size="small" wrap>
                <Tag color="green">{dayjs(fechasActividades[0]).format('DD/MM/YYYY')}</Tag>
                <span className="text-gray-500">→</span>
                <Tag color="green">{dayjs(fechasActividades[1]).format('DD/MM/YYYY')}</Tag>
                <Tag color="cyan">
                  {dayjs(fechasActividades[1]).diff(dayjs(fechasActividades[0]), 'day')} días
                </Tag>
              </Space>
            </div>
          ) : (
            <Alert
              message="Periodo de actividades sin definir"
              description="Vuelve al paso de Fechas para indicar cuándo comienza y termina el taller."
              type="warning"
              showIcon
            />
          )}
        </div>
      </Card>

      {/* Info sobre suscripciones */}
      <Card title="ℹ️ Sobre las suscripciones" size="small">
        <Alert
          message="Las suscripciones son individuales"
          description={
            <div className="space-y-2 text-sm">
              <p>Cada usuario tendrá su propia fecha de inicio y término:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Inicio:</strong> El día en que realiza el pago</li>
                <li><strong>Término:</strong> Se calcula según el tipo de duración configurado ({getTipoDuracionLabel(tipoDuracionSuscripcion)})</li>
              </ul>
              {tipoDuracionSuscripcion === 'mes_calendario' && (
                <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                  <strong>Ejemplo:</strong> Si un usuario paga el 15 de marzo con plan mensual, su suscripción termina el 15 de abril
                </div>
              )}
              {tipoDuracionSuscripcion === 'fin_de_mes' && (
                <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                  <strong>Ejemplo:</strong> Si un usuario paga el 15 de marzo con plan mensual, su suscripción termina el 31 de marzo
                </div>
              )}
              {tipoDuracionSuscripcion === 'mes_corrido' && (
                <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                  <strong>Ejemplo:</strong> Si un usuario paga el 15 de marzo con plan mensual, su suscripción termina el 14 de abril (30 días exactos)
                </div>
              )}
            </div>
          }
          type="info"
          showIcon
        />
      </Card>

      {/* Modalidades de pago */}
      <Card title="💰 Configuración de pago" size="small">
        {esGratuito ? (
          <Alert
            message="Taller gratuito"
            description="Este taller no tiene costo para los usuarios"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : planesActivos.length > 0 ? (
          <div className="space-y-3">
            {planesActivos.map(({ plan, ...config }) => (
              <Card key={plan} size="small" type="inner" title={<span className="uppercase font-semibold">{plan}</span>}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {config.valorParticular > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Particular</div>
                      <div className="font-semibold text-blue-600">
                        ${config.valorParticular.toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                  {config.valorVecino > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Vecino</div>
                      <div className="font-semibold text-blue-600">
                        ${config.valorVecino.toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                  {config.valorAdultoMayor > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Adulto mayor</div>
                      <div className="font-semibold text-blue-600">
                        ${config.valorAdultoMayor.toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                  {config.valorFuncionarios > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Funcionarios</div>
                      <div className="font-semibold text-blue-600">
                        ${config.valorFuncionarios.toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                  {config.matricula > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">Matrícula</div>
                      <div className="font-semibold text-orange-600">
                        ${config.matricula.toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Empty 
            description="No hay planes de pago configurados" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Gestión de cupos */}
      <Card title="🎫 Gestión de cupos" size="small">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Liberar cupos por baja:</span>
          <Tag color={liberarCuposPorBaja ? "success" : "default"} className="text-sm">
            {liberarCuposPorBaja ? "✓ Activado" : "✗ Desactivado"}
          </Tag>
        </div>
        {liberarCuposPorBaja && (
          <div className="mt-3 text-xs text-gray-600 bg-blue-50 p-2 rounded">
            Los cupos se liberarán automáticamente cuando un usuario se dé de baja
          </div>
        )}
      </Card>

      {/* Confirmación */}
      <Alert
        message="Todo listo para crear el taller"
        description="Al continuar, se creará el taller y podrás agregar horarios en el siguiente paso"
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
      />
    </div>
  );
}
